import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { selectAccessToken } from '../../../slices/staffSlice';
import { HugeiconsIcon } from '@hugeicons/react';
import { Layers01Icon, Alert01Icon, CheckmarkCircle01Icon, ArrowUpRight01Icon, UserGroupIcon } from '@hugeicons/core-free-icons';

const PERIOD_LABEL = { today: 'today', month: 'this month', quarter: 'this quarter', ytd: 'year to date' };

// severity ramp (risk) — kept separate from the brand accent
const SEV = ['#1a9d54', '#74a828', '#c99109', '#dd7a10', '#e5561f', '#cf2a26', '#7a879c'];
// categorical (products / channel / customer mix)
const CAT = ['#1e3a6e', '#0e7aa6', '#6d3bd1', '#c0398c', '#8493a9', '#2f8f6b'];

const ghs = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1e6) return `GHS ${(v / 1e6).toFixed(2)}M`;
    if (Math.abs(v) >= 1e3) return `GHS ${Math.round(v / 1e3)}k`;
    return `GHS ${Math.round(v)}`;
};
const dow = (iso) => new Date(iso).toLocaleDateString('en-GB', { weekday: 'short' });
const parColor = (p) => (p > 10 ? '#cf2a26' : p >= 5 ? '#b5710a' : '#157f3c');

function Kpi({ label, value, sub, color, icon, tint }) {
    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-4">
            <div className="flex items-start gap-3">
                {icon && (
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tint}`}>
                        <HugeiconsIcon icon={icon} size={17} />
                    </div>
                )}
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
                    <p className="text-2xl font-bold mt-1 tabular-nums" style={color ? { color } : undefined}>{value}</p>
                    {sub && <p className="text-[11px] text-gray-400 mt-1 tabular-nums">{sub}</p>}
                </div>
            </div>
        </div>
    );
}

function Card({ title, hint, children, className = '' }) {
    return (
        <div className={`bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-4 ${className}`}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
                {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
            </div>
            {children}
        </div>
    );
}

function Donut({ segments, size = 104 }) {
    const r = 46, C = 2 * Math.PI * r;
    const total = segments.reduce((s, x) => s + (x.value || 0), 0) || 1;
    let offset = 0;
    return (
        <svg viewBox="0 0 120 120" width={size} height={size}>
            <g transform="rotate(-90 60 60)" fill="none" strokeWidth="16">
                {segments.map((s, i) => {
                    const len = (Math.max(0, s.value) / total) * C;
                    const gap = len > 6 ? 2 : 0;
                    const el = (
                        <circle key={i} cx="60" cy="60" r={r} stroke={s.color}
                            strokeDasharray={`${Math.max(0, len - gap)} ${C - Math.max(0, len - gap)}`}
                            strokeDashoffset={-offset} />
                    );
                    offset += len;
                    return el;
                })}
            </g>
        </svg>
    );
}

function Legend({ items }) {
    return (
        <div className="flex flex-col gap-2 text-[12px] text-gray-500 dark:text-gray-400">
            {items.map((it, i) => (
                <span key={i} className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: it.color }} />{it.label}
                </span>
            ))}
        </div>
    );
}

function MetricBar({ label, value, color }) {
    return (
        <div className="py-2 border-b last:border-b-0 border-gray-100 dark:border-gray-700/60">
            <div className="flex justify-between text-[12.5px] mb-1.5"><b className="font-semibold text-gray-700 dark:text-gray-200">{label}</b><span className="text-gray-400 tabular-nums">{value}%</span></div>
            <div className="h-1.5 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden"><div className="h-full rounded" style={{ width: `${Math.min(100, value)}%`, background: color }} /></div>
        </div>
    );
}

// ── Loading skeleton ─────────────────────────────────────────────────────────
// Mirrors the real layout card-for-card so nothing jumps when the data fades in.

function Bone({ className = '' }) {
    return <div className={`shimmer rounded-md ${className}`} />;
}

function SkeletonCard({ className = '', children }) {
    return (
        <div className={`bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-4 ${className}`}>
            <div className="flex items-center justify-between mb-4">
                <Bone className="h-3.5 w-32" />
                <Bone className="h-3 w-16" />
            </div>
            {children}
        </div>
    );
}

function SkeletonRows({ count, className = 'h-3' }) {
    return (
        <div className="space-y-3">
            {Array.from({ length: count }).map((_, i) => <Bone key={i} className={`${className} w-full`} />)}
        </div>
    );
}

function DonutSkeleton({ size = 'w-24 h-24', lines = 4 }) {
    return (
        <div className="flex items-center gap-4">
            <div className={`shimmer rounded-full shrink-0 ${size}`} />
            <div className="flex-1 space-y-2.5">
                {Array.from({ length: lines }).map((_, i) => <Bone key={i} className="h-3 w-full" />)}
            </div>
        </div>
    );
}

function SectionLabel() {
    return <Bone className="h-3 w-28 mb-3" />;
}

function DashboardSkeleton() {
    return (
        <div className="space-y-6" aria-busy="true">
            <span className="sr-only" role="status">Loading dashboard metrics…</span>

            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-4">
                        <div className="flex items-start gap-3">
                            <div className="shimmer w-9 h-9 rounded-xl shrink-0" />
                            <div className="flex-1 space-y-2">
                                <Bone className="h-2.5 w-3/4" />
                                <Bone className="h-6 w-2/3" />
                                <Bone className="h-2.5 w-1/2" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Portfolio & risk */}
            <div>
                <SectionLabel />
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <SkeletonCard className="lg:col-span-5">
                        <Bone className="h-5 w-full mb-3" />
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                            {Array.from({ length: 6 }).map((_, i) => <Bone key={i} className="h-3 w-full" />)}
                        </div>
                    </SkeletonCard>
                    <SkeletonCard className="lg:col-span-4"><DonutSkeleton /></SkeletonCard>
                    <SkeletonCard className="lg:col-span-3"><SkeletonRows count={4} className="h-4" /></SkeletonCard>
                </div>
            </div>

            {/* Collections */}
            <div>
                <SectionLabel />
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <SkeletonCard className="lg:col-span-5">
                        <Bone className="h-6 w-40 mb-3" />
                        <div className="flex items-end gap-2 h-24">
                            {[55, 80, 40, 95, 65, 30, 70].map((h, i) => (
                                <div key={i} className="shimmer flex-1 rounded-[4px]" style={{ height: `${h}%` }} />
                            ))}
                        </div>
                    </SkeletonCard>
                    <SkeletonCard className="lg:col-span-4">
                        <div className="space-y-5 pt-1">
                            <SkeletonRows count={2} className="h-2.5" />
                            <SkeletonRows count={2} className="h-2.5" />
                        </div>
                    </SkeletonCard>
                    <SkeletonCard className="lg:col-span-3"><DonutSkeleton size="w-[92px] h-[92px]" lines={2} /></SkeletonCard>
                </div>
            </div>

            {/* Pipeline & arrears */}
            <div>
                <SectionLabel />
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <SkeletonCard className="lg:col-span-4"><SkeletonRows count={4} /></SkeletonCard>
                    <SkeletonCard className="lg:col-span-8"><SkeletonRows count={5} className="h-4" /></SkeletonCard>
                </div>
            </div>
        </div>
    );
}

function DashboardMetrics({ stats, period = 'month' }) {
    const accessToken = useSelector(selectAccessToken);
    const [data, setData] = useState(null);
    const [state, setState] = useState('loading');
    // bumped on every successful load so the content re-runs its fade-in
    const [version, setVersion] = useState(0);

    const load = useCallback(async (refresh) => {
        setState('loading');
        const qs = `?period=${period}${refresh ? '&refresh=true' : ''}`;
        const res = await fetchRequests(`/reports/staff/dashboard-metrics${qs}`, 'get', authStaff(accessToken), {}, {});
        if (!res || res.err) { setState('error'); return; }
        setData(res); setState('ready'); setVersion((v) => v + 1);
    }, [accessToken, period]);

    useEffect(() => { load(false); }, [load]);

    if (state === 'loading' && !data) return <DashboardSkeleton />;
    if (state === 'error' && !data) return (
        <div className="text-sm text-gray-400 py-6 text-center">
            Portfolio metrics unavailable. <button className="text-[#1e3a6e] dark:text-blue-400 underline" onClick={() => load(true)}>Retry</button>
        </div>
    );
    if (!data) return null;

    const outstanding = data.portfolio?.outstanding || 0;
    const par30 = data.risk?.par30 ?? 0;
    const col = data.collections;
    const cm = data.customerMix;
    const activeBorrowers = cm?.activeBorrowers ?? data.portfolio?.numOutstanding ?? 0;
    const verifiedPct = stats?.clientCount > 0 ? Math.round((stats.verifiedClientCount / stats.clientCount) * 100) : null;

    const ag = data.risk?.aging || {};
    const agingBuckets = [
        { k: 'Current', v: ag.prinCur || 0, c: SEV[0] },
        { k: '1–30', v: ag.prin30 || 0, c: SEV[1] },
        { k: '31–60', v: ag.prin60 || 0, c: SEV[2] },
        { k: '61–90', v: ag.prin90 || 0, c: SEV[3] },
        { k: '91–180', v: (ag.prin120 || 0) + (ag.prin150 || 0) + (ag.prin180 || 0), c: SEV[4] },
        { k: '180+', v: (ag.prin365 || 0) + (ag.prinInf || 0), c: SEV[5] },
        { k: 'Rescheduled', v: ag.prinResched || 0, c: SEV[6] },
    ];
    const agingTotal = agingBuckets.reduce((s, b) => s + b.v, 0) || 1;

    const products = (data.products || []).slice(0, 5);
    const due = data.repaymentsDue;
    const dueMax = due ? Math.max(1, ...due.days.map((d) => d.amount)) : 1;
    const arrears = data.arrears || [];

    const pipeline = stats ? [
        { k: 'Pending review', v: stats.pendingCount || 0, c: CAT[0] },
        { k: 'Active', v: stats.activeCount || 0, c: CAT[0] },
        { k: 'Completed', v: stats.completedCount || 0, c: '#1a9d54' },
        { k: 'Rejected', v: stats.rejectedCount || 0, c: '#cf2a26' },
    ] : [];
    const pipMax = Math.max(1, ...pipeline.map((p) => p.v));

    const statusLabel = (d) => (d > 90 ? ['crit', '90+ days'] : ['warn', `${d} days`]);

    return (
        // outer: dims old numbers while a period switch / refresh is in flight
        // inner: keyed so each new dataset fades in
        <div className={`transition-opacity duration-200 ${state === 'loading' ? 'opacity-60' : ''}`} aria-busy={state === 'loading'}>
        <div key={version} className="space-y-6 animate-fade-in motion-reduce:animate-none">
            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Kpi label="Portfolio outstanding" value={ghs(outstanding)} sub={`${data.portfolio?.numOutstanding || 0} loans`}
                    icon={Layers01Icon} tint="bg-blue-50 text-[#1e3a6e] dark:bg-blue-500/10 dark:text-blue-400" />
                <Kpi label="PAR > 30 days" value={`${par30}%`} color={parColor(par30)} sub={`PAR>90: ${data.risk?.par90 ?? 0}%`}
                    icon={Alert01Icon} tint="bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400" />
                <Kpi label="Collection efficiency" value={col?.collectionEfficiency != null ? `${col.collectionEfficiency}%` : '—'} sub="collected vs due"
                    icon={CheckmarkCircle01Icon} tint="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400" />
                <Kpi label="Disbursed" value={col ? ghs(col.disbursed) : '—'} sub={PERIOD_LABEL[period] || 'this month'}
                    icon={ArrowUpRight01Icon} tint="bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400" />
                <Kpi label="Active borrowers" value={Number(activeBorrowers).toLocaleString()} sub="active"
                    icon={UserGroupIcon} tint="bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400" />
            </div>

            {/* Portfolio & risk */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Portfolio &amp; risk</p>
                    <button onClick={() => load(true)} className="text-[11px] text-gray-400 hover:text-[#1e3a6e] dark:hover:text-blue-400">
                        {state === 'loading' ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <Card title="Delinquency aging" hint="share of portfolio" className="lg:col-span-5">
                        <div className="flex gap-[2px] h-5 mb-3">
                            {agingBuckets.map((b, i) => (
                                <div key={i} title={`${b.k}: ${ghs(b.v)}`} style={{ flex: Math.max(0.02, b.v / agingTotal), background: b.c, borderRadius: 3 }} />
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                            {agingBuckets.map((b, i) => (
                                <span key={i} className="inline-flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: b.c }} />
                                    <span className="text-gray-500 dark:text-gray-400">{b.k}</span>
                                    <span className="ml-auto font-semibold tabular-nums">{Math.round((b.v / agingTotal) * 100)}%</span>
                                </span>
                            ))}
                        </div>
                    </Card>

                    <Card title="By product" hint="outstanding" className="lg:col-span-4">
                        <div className="flex items-center gap-4">
                            <Donut segments={products.map((p, i) => ({ value: p.outstanding, color: CAT[i % CAT.length] }))} />
                            <Legend items={products.map((p, i) => ({ color: CAT[i % CAT.length], label: `${p.type} · ${p.share}%` }))} />
                        </div>
                    </Card>

                    <Card title="Customer mix" hint="portfolio" className="lg:col-span-3">
                        {cm ? (
                            <>
                                <MetricBar label="Women borrowers" value={cm.womenPct ?? 0} color={CAT[3]} />
                                <MetricBar label="Youth (≤ 35)" value={cm.youthPct ?? 0} color={CAT[2]} />
                                {verifiedPct != null && <MetricBar label="Verified clients" value={verifiedPct} color="#157f3c" />}
                                <MetricBar label="Repeat borrowers" value={cm.repeatPct ?? 0} color={CAT[0]} />
                            </>
                        ) : <p className="text-sm text-gray-400">No data.</p>}
                    </Card>
                </div>
            </div>

            {/* Collections */}
            <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Collections</p>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <Card title="Repayments due · next 7 days" hint="expected" className="lg:col-span-5">
                        {due ? (
                            <>
                                <div className="flex items-baseline gap-2 mb-2">
                                    <span className="text-xl font-bold tabular-nums">{ghs(due.total)}</span>
                                    <span className="text-[12px] text-gray-400 tabular-nums">across {due.count} loans</span>
                                </div>
                                <div className="flex items-end gap-2 h-24">
                                    {due.days.map((d, i) => (
                                        <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${ghs(d.amount)} · ${d.count} loans`}>
                                            <div className="w-full rounded-[4px]" style={{ height: `${Math.max(4, (d.amount / dueMax) * 76)}px`, background: '#1e3a6e' }} />
                                            <span className="text-[10px] text-gray-400">{dow(d.date)}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : <p className="text-sm text-gray-400">No data.</p>}
                    </Card>

                    <Card title="Disbursed vs collected" hint={PERIOD_LABEL[period] || 'this month'} className="lg:col-span-4">
                        {col ? (
                            <div className="space-y-4 pt-1">
                                {[['Disbursed', col.disbursed, CAT[0]], ['Collected', col.collected, CAT[1]]].map(([lbl, val, c], i) => {
                                    const mx = Math.max(1, col.disbursed, col.collected);
                                    return (
                                        <div key={i}>
                                            <div className="flex justify-between text-[12px] mb-1"><span className="text-gray-500">{lbl}</span><span className="font-semibold tabular-nums">{ghs(val)}</span></div>
                                            <div className="h-2.5 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden"><div className="h-full rounded" style={{ width: `${(val / mx) * 100}%`, background: c }} /></div>
                                        </div>
                                    );
                                })}
                                {col.commission > 0 && <p className="text-[11px] text-gray-400 tabular-nums pt-1">Commission earned: {ghs(col.commission)}</p>}
                            </div>
                        ) : <p className="text-sm text-gray-400">No data.</p>}
                    </Card>

                    <Card title="Repayment channel" className="lg:col-span-3">
                        {col?.channel ? (
                            <div className="flex items-center gap-3">
                                <Donut size={92} segments={[{ value: col.channel.momo, color: CAT[0] }, { value: col.channel.bank, color: CAT[1] }]} />
                                <Legend items={[
                                    { color: CAT[0], label: `MoMo · ${ghs(col.channel.momo)}` },
                                    { color: CAT[1], label: `Bank · ${ghs(col.channel.bank)}` },
                                ]} />
                            </div>
                        ) : <p className="text-sm text-gray-400">No data.</p>}
                    </Card>
                </div>
            </div>

            {/* Pipeline & arrears */}
            <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Pipeline &amp; arrears</p>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <Card title="Loan pipeline" hint="current" className="lg:col-span-4">
                        {/* pipeline counts come from a separate stats request that can land later */}
                        {!stats && <SkeletonRows count={4} />}
                        <div className="space-y-2.5">
                            {pipeline.map((p, i) => (
                                <div key={i} className="grid grid-cols-[110px_1fr_44px] items-center gap-2 text-[12.5px]">
                                    <span className="text-gray-500">{p.k}</span>
                                    <span className="h-3 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden"><span className="block h-full rounded" style={{ width: `${(p.v / pipMax) * 100}%`, background: p.c }} /></span>
                                    <span className="text-right font-semibold tabular-nums">{p.v}</span>
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card title="Arrears watchlist" hint={`${arrears.length} shown`} className="lg:col-span-8">
                        {arrears.length === 0 ? <p className="text-sm text-gray-400 py-4">No loans in arrears. 🎉</p> : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-[12.5px]">
                                    <thead>
                                        <tr className="text-[10.5px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                            <th className="text-left px-3 py-2">Client</th>
                                            <th className="text-left px-3 py-2">Product</th>
                                            <th className="text-left px-3 py-2">Rel. manager</th>
                                            <th className="text-right px-3 py-2">Outstanding</th>
                                            <th className="text-right px-3 py-2">Days</th>
                                            <th className="text-left px-3 py-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {arrears.map((a, i) => {
                                            const [tone, label] = statusLabel(a.days);
                                            const cls = tone === 'crit' ? 'text-red-600 bg-red-50 dark:bg-red-500/10' : 'text-amber-600 bg-amber-50 dark:bg-amber-500/10';
                                            return (
                                                <tr key={i} className="border-b border-gray-50 dark:border-gray-700/50">
                                                    <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{a.name}</td>
                                                    <td className="px-3 py-2.5 text-gray-500">{a.product}</td>
                                                    <td className="px-3 py-2.5 text-gray-500">{a.relManager}</td>
                                                    <td className="px-3 py-2.5 text-right tabular-nums">{ghs(a.outstanding)}</td>
                                                    <td className="px-3 py-2.5 text-right tabular-nums">{a.days}</td>
                                                    <td className="px-3 py-2.5"><span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{label}</span></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
        </div>
    );
}

export default DashboardMetrics;
